import { SITE_TUNING } from "./siteTuning";

type Point = { x: number; y: number };
type LaunchGeometry = {
    width: number;
    height: number;
    tokenWidth: number;
    tokenHeight: number;
    targetX: number;
    targetY: number;
    index: number;
    sideBounce: boolean;
    floor: boolean;
    compact: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const mix = (from: number, to: number, progress: number) => from + (to - from) * progress;
const tuning = SITE_TUNING.randomTeams.animation;

/** Blend a restrained arc into each straight flight; direction changes only at contact. */
export const createRandomTeamsLaunchMotion = (geometry: LaunchGeometry) => {
    const { width, height, tokenWidth, tokenHeight, targetX, targetY, index, sideBounce, floor, compact } = geometry;
    const fromRight = sideBounce || index % 2 === 0;
    const spawnX = fromRight ? width + tokenWidth * 1.5 : -tokenWidth * 2.5;
    const direction = fromRight ? -1 : 1;
    const entryDistance = Math.abs(targetX - spawnX);
    const headroom = Math.max(0, targetY - tokenHeight * 0.12);
    let contact = 0;
    let position: (progress: number) => Point;

    if (floor) {
        const lift = Math.min(headroom, tokenHeight * tuning.trayArcHeightTokens) * tuning.flightCurveStrength;
        const startY = targetY - lift * 0.6;
        position = (progress) => ({
            x: mix(spawnX, targetX, progress),
            y: mix(startY, targetY, progress) - 4 * lift * progress * (1 - progress)
        });
    } else if (sideBounce) {
        const wallX = 2;
        const returnDistance = Math.max(0, targetX - wallX);
        const lift = Math.min(headroom, tuning.sideArcMaxHeightPx,
            Math.max(tuning.sideArcMinHeightPx, tokenHeight * 0.3 + returnDistance * 0.08));
        const impactY = targetY + Math.min(tokenHeight * 0.18, Math.max(0, height - tokenHeight - targetY));
        const startY = Math.max(0, targetY - lift * 0.8);
        contact = (spawnX - wallX) / (spawnX - wallX + returnDistance / tuning.wallHorizontalRetention);
        // A slot adjacent to the wall still gets time to release its compression.
        contact = Math.min(contact, tuning.sideMaxContactOffset);
        position = (progress) => {
            if (progress <= contact) {
                const entry = progress / contact;
                return { x: mix(spawnX, wallX, entry),
                    y: mix(startY, impactY, mix(entry, entry * entry, tuning.flightCurveStrength)) };
            }
            const rebound = (progress - contact) / (1 - contact);
            return {
                x: mix(wallX, targetX, rebound),
                y: mix(impactY, targetY, rebound) - 4 * lift * rebound * (1 - rebound) * tuning.flightCurveStrength
            };
        };
    } else {
        const floorY = Math.max(targetY, height - tokenHeight - Math.min(52, tokenHeight * 0.65));
        const lift = Math.min(headroom, tokenHeight * tuning.floorArcHeightTokens);
        const reboundHeight = Math.max(1, floorY - targetY + lift);
        const startY = Math.min(tokenHeight * 0.4, targetY - lift - tokenHeight * 0.12);
        // Keep the ballistic contact timing and horizontal momentum, but flatten
        // each arc toward its chord so the change of direction sells the impact.
        const fallTime = Math.sqrt(2 * Math.max(1, floorY - startY));
        const riseTime = Math.sqrt(2 * reboundHeight);
        const reboundTime = riseTime + Math.sqrt(2 * Math.max(0, lift));
        const totalTime = fallTime + reboundTime;
        const velocityX = (targetX - spawnX) / (fallTime + tuning.floorHorizontalRetention * reboundTime);
        const impactX = spawnX + velocityX * fallTime;
        contact = fallTime / totalTime;
        position = (progress) => {
            const time = progress * totalTime;
            if (time <= fallTime) {
                return { x: spawnX + velocityX * time,
                    y: mix(mix(startY, floorY, time / fallTime), startY + time * time / 2, tuning.flightCurveStrength) };
            }
            const rebound = time - fallTime;
            return {
                x: impactX + velocityX * tuning.floorHorizontalRetention * rebound,
                y: mix(mix(floorY, targetY, rebound / reboundTime),
                    floorY - riseTime * rebound + rebound * rebound / 2, tuning.flightCurveStrength)
            };
        };
    }

    const samples = Array.from({ length: tuning.flightSampleCount + 1 }, (_, i) => position(i / tuning.flightSampleCount));
    const distance = samples.slice(1).reduce((sum, point, i) => sum + Math.hypot(point.x - samples[i].x, point.y - samples[i].y), 0);
    const speed = floor ? tuning.launchPixelsPerMs * 1.28 : sideBounce ? tuning.sideBounceLaunchPixelsPerMs : tuning.launchPixelsPerMs;
    const minDuration = floor ? 540 : sideBounce ? tuning.sideBounceMinLaunchDurationMs
        : entryDistance <= tuning.shortEntryDistanceThresholdPx ? tuning.shortEntryMinLaunchDurationMs : tuning.minLaunchDurationMs;
    const maxDuration = floor ? 920 : sideBounce ? tuning.sideBounceMaxLaunchDurationMs : tuning.maxLaunchDurationMs;
    const duration = Math.round(clamp(distance / speed, minDuration, maxDuration) * tuning.launchTimeScale);
    const landingOffset = 1 - tuning.landingSettleMs / duration;
    const impactOffset = floor ? null : contact * landingOffset;
    const contactWindow = tuning.contactCompressionMs / duration;
    const offsets = new Set(Array.from({ length: tuning.flightSampleCount + 1 }, (_, i) => i / tuning.flightSampleCount * landingOffset));
    if (impactOffset !== null) {
        [impactOffset - contactWindow, impactOffset, impactOffset + contactWindow, impactOffset + contactWindow * 2]
            .forEach(offset => offsets.add(clamp(offset, 0, landingOffset)));
    }
    [landingOffset, mix(landingOffset, 1, 0.4), mix(landingOffset, 1, 0.75), 1].forEach(offset => offsets.add(offset));

    const keyframes: Keyframe[] = [...offsets].sort((a, b) => a - b).map(offset => {
        const progress = Math.min(1, offset / landingOffset);
        const point = offset >= landingOffset ? { x: targetX, y: targetY } : position(progress);
        const before = position(Math.max(0, progress - 0.002));
        const after = position(Math.min(1, progress + 0.002));
        const slopeTilt = Math.atan2(after.y - before.y, Math.abs(after.x - before.x) + 0.001) * 180 / Math.PI;
        const rotation = offset >= landingOffset ? 0 : clamp(slopeTilt * direction * tuning.velocityTiltRatio, -14, 14);
        const compression = impactOffset === null ? 0 : Math.max(0, 1 - Math.abs(offset - impactOffset) / contactWindow);
        const settle = offset < landingOffset ? 0 : (offset - landingOffset) / (1 - landingOffset);
        const landingCompression = offset >= landingOffset ? Math.cos(settle * Math.PI * 2) * Math.exp(-settle * 5) * (1 - settle) : 0;
        const squash = compact ? 0.18 : 0.22;
        const scaleX = 1 + compression * (sideBounce ? -squash : squash) + landingCompression * 0.07;
        const scaleY = 1 + compression * (sideBounce ? 0.14 : -0.18) - landingCompression * 0.07;
        return {
            offset,
            transform: `translate(${point.x}px, ${point.y}px) rotate(${rotation * (1 - compression)}deg) scale(${scaleX}, ${scaleY})`,
            opacity: Math.min(1, 0.25 + offset * 12)
        };
    });
    return { keyframes, duration, impactOffset, landingOffset, transformOrigin: sideBounce && !floor ? "0 50%" : "50% 100%" };
};
